import React from 'react';
import { ThemeProvider } from 'styled-components';

import { useAppSelector } from 'App/State/Store';
import IconAgreementDocument from 'Common/Assets/images/icon-agreement-document.svg';
import Paragraph from 'Common/UI/Text/Paragraph';
import HelpExternalButton from 'Common/UI/Button/HelpExternalButton';
import SpacedBox from 'Common/UI/Layout/SpacedBox';
import GenericButton from 'Common/UI/Button/GenericButton';
import { Label } from 'Common/UI/Text/Label';
import getNames from 'Common/Utils/getNames';
import {
  DocumentCardStatus,
  DOCUMENT_CARD_REQUIRED_SECTIONS_INCOMPLETE,
  DOCUMENT_CARD_REQUIRED_SECTIONS_COMPLETE,
  DOCUMENT_CARD_COMPLETE,
  DOCUMENT_CARD_SELF_REQUESTED,
  DOCUMENT_CARD_OTHER_REQUESTED,
  DOCUMENT_CARD_PAYMENT_REQUIRED,
  DOC_TYPE_PARENTING_AGREEMENT,
} from 'Common/constants';
import { getPricing } from 'Common/Utils/pricing';
import IconClock from 'Common/Assets/images/icon-clock.svg';
import TextButton from 'Common/UI/Button/TextButton';
import { Column } from 'Common/UI/Layout/Flex';
import { orangeTheme, cardThemes } from 'Common/Utils/theme';
import DownloadButton from 'Common/UI/Button/DownloadButton';
import colours from 'Common/Utils/colours';
import { ODRS_PAYMENT_REVIEW_ORDER_ROUTE } from 'Common/routes';
import isSafari from 'Common/Utils/isSafari';
import IconOther from 'Common/Assets/images/icon-users-other.svg';
import {
  CardWrapper,
  CardHeader,
  TickText,
  TickItem,
  CardContent,
  CardFooterWithDivider,
  CardFooterGrey,
  WaitingLabel,
  SafariDownloadNotice,
} from '../common';

type Props = {
  isFree: boolean;
  isPaid: boolean;
  status: DocumentCardStatus;
  setPartyFlags: (flags: Record<string, boolean>) => Promise<void>;
  openAgreementFunctions: Record<string, () => Promise<void>>;
  navigate: (route: string, state?: any) => void;
};

const ParentingAgreement: React.FC<Props> = ({
  isFree = false,
  isPaid = false,
  status = DOCUMENT_CARD_REQUIRED_SECTIONS_INCOMPLETE,
  setPartyFlags,
  openAgreementFunctions,
  navigate,
}) => {
  const matter = useAppSelector(state => state.matter);
  const { table: pricingTable } = getPricing(matter);

  const names = getNames();

  let price = `$${pricingTable[DOC_TYPE_PARENTING_AGREEMENT].priceExGST} +GST`;

  if (isFree) {
    price = 'Free';
  } else if (isPaid) {
    price = 'Paid';
  }

  const sharedContent = (
    <>
      <CardHeader
        icon={IconAgreementDocument}
        subTitle="Agreement"
        price={price}
        title="Parenting agreement"
      />
      <CardContent>
        <Paragraph>
          This document sets out your parenting arrangements. This includes
          things like nights with each of you, handovers, special occasions,
          school holidays, travel and health.
        </Paragraph>
        <SpacedBox marginBottom={8}>
          <TickItem>
            <TickText>Covers the essentials</TickText>
          </TickItem>
          <TickItem>
            <TickText>
              Lists the parenting arrangements you select on amica
            </TickText>
          </TickItem>
          <TickItem>
            <TickText>Gives a written record of your agreement</TickText>
          </TickItem>
        </SpacedBox>
        <HelpExternalButton href="https://www.amica.gov.au/info-about-separating/parenting-arrangements">
          Learn more
        </HelpExternalButton>
      </CardContent>
    </>
  );

  switch (status) {
    case DOCUMENT_CARD_REQUIRED_SECTIONS_INCOMPLETE:
    default:
      return <CardWrapper>{sharedContent}</CardWrapper>;
    case DOCUMENT_CARD_REQUIRED_SECTIONS_COMPLETE:
      return (
        <CardWrapper>
          {sharedContent}
          <CardFooterWithDivider>
            <GenericButton
              fullWidth
              onClick={() => setPartyFlags({ canExportParenting: true })}
            >
              Create parenting agreement
            </GenericButton>

            <SpacedBox marginTop={8}>
              <Label>
                {names.other} will also need to agree to creating a property
                agreement
              </Label>
            </SpacedBox>
          </CardFooterWithDivider>
        </CardWrapper>
      );
    case DOCUMENT_CARD_SELF_REQUESTED:
      return (
        <CardWrapper borderColour={colours.midPurple}>
          {sharedContent}
          <CardFooterGrey>
            <WaitingLabel>
              <img src={IconClock} alt="" /> Waiting for {names.other} to agree
              to create your parenting agreement
            </WaitingLabel>
            <GenericButton
              subtle
              small
              onClick={() => setPartyFlags({ canExportParenting: false })}
            >
              Undo
            </GenericButton>
          </CardFooterGrey>
        </CardWrapper>
      );
    case DOCUMENT_CARD_OTHER_REQUESTED:
      return (
        <CardWrapper borderColour={colours.midTeal}>
          {sharedContent}
          <CardFooterGrey>
            <WaitingLabel>
              <img src={IconOther} alt="" /> {names.other} has requested to
              create a parenting agreement
            </WaitingLabel>
            <Column>
              <ThemeProvider theme={cardThemes.other}>
                <GenericButton
                  fullWidth
                  onClick={() => setPartyFlags({ canExportParenting: true })}
                >
                  Agree to create parenting agreement
                </GenericButton>
                <TextButton
                  fullWidth
                  onClick={() =>
                    setPartyFlags({ notReadyToExportParenting: true })
                  }
                >
                  No thanks
                </TextButton>
              </ThemeProvider>
            </Column>
          </CardFooterGrey>
        </CardWrapper>
      );
    case DOCUMENT_CARD_PAYMENT_REQUIRED:
      return (
        <CardWrapper>
          {sharedContent}
          <CardFooterWithDivider>
            <ThemeProvider theme={orangeTheme}>
              <GenericButton
                fullWidth
                onClick={() => navigate(ODRS_PAYMENT_REVIEW_ORDER_ROUTE)}
              >
                Pay for document
              </GenericButton>
            </ThemeProvider>

            <SpacedBox marginTop={4}>
              <Label>
                You will be able to download this document once either of you
                pay for it
              </Label>
            </SpacedBox>
          </CardFooterWithDivider>
        </CardWrapper>
      );
    case DOCUMENT_CARD_COMPLETE:
      return (
        <CardWrapper>
          {sharedContent}
          <CardFooterWithDivider>
            <DownloadButton onClick={openAgreementFunctions.parentingAgreement}>
              Download parenting agreement
            </DownloadButton>

            {isSafari() && (
              <SafariDownloadNotice>
                Downloading a document can sometimes take up to 15 seconds to
                load.
              </SafariDownloadNotice>
            )}
          </CardFooterWithDivider>
        </CardWrapper>
      );
  }
};

export default ParentingAgreement;
